import { PaletteMode as PaletteModeType, useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { MAPBOX_DARK, MAPBOX_LIGHT, usePreferredMapStyle } from 'components';
import { getDesignTokens, getThemedComponents } from 'context/theme';
import { useLocalStorage } from 'hooks';

// https://github.com/mui/material-ui/blob/master/docs/src/modules/brandingTheme.ts
// https://github.com/mui/material-ui/blob/master/docs/src/modules/components/ThemeContext.js

interface ColorModeContextValue {
  mode: 'light' | 'dark';
  toggleColorMode: () => void;
}

export const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export function ThemeProvider(props: any) {
  const { children } = props;
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [storageMode, setStorageMode] = useLocalStorage<'light' | 'dark' | undefined>(
    'iDemand-theme-mode'
  );
  const [mode, setMode] = useState<'light' | 'dark'>(
    storageMode ?? (prefersDarkMode ? 'dark' : 'light')
  );

  const [, setMapTheme] = usePreferredMapStyle();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (storageMode) return;

      setStorageMode(mode);
    }
  }, [prefersDarkMode, mode, storageMode, setStorageMode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setStorageMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));

        setMode((prevMode: PaletteModeType) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';

          const newMapTheme = newMode === 'dark' ? MAPBOX_DARK : MAPBOX_LIGHT;
          setMapTheme(newMapTheme);

          return newMode;
        });
      },
    }),
    [setStorageMode, setMapTheme]
  );

  const theme = useMemo(() => {
    const brandingDesignTokens = getDesignTokens(mode);
    const mergedPalette = deepmerge(brandingDesignTokens.palette, {});
    let updatedTheme = createTheme({
      palette: {
        ...mergedPalette,
        mode: mode,
      },
      spacing: 4,
      shape: {
        borderRadius: 10,
      },
    });
    updatedTheme = deepmerge(updatedTheme, getThemedComponents(updatedTheme));
    updatedTheme = responsiveFontSizes(updatedTheme);

    return updatedTheme;
  }, [mode]);

  return (
    <MuiThemeProvider theme={theme}>
      <ColorModeContext.Provider value={{ toggleColorMode: colorMode.toggleColorMode, mode }}>
        <CssBaseline enableColorScheme />
        {children}
      </ColorModeContext.Provider>
    </MuiThemeProvider>
  );
}

export function useChangeTheme() {
  const colorMode = useContext(ColorModeContext);

  if (colorMode === undefined) {
    throw new Error('useChangeTheme must be used within ThemeProvider');
  }

  return colorMode;
}
